<template>
  <div class="multilingual-text-field">
    <div class="multilingual-text-field__heading">
      <h5 class="mb-2">
        {{ label }}
        <v-chip v-if="required" class="ma-2" x-small outlined color="primary">
          {{ $t("form.mandatory") }}
        </v-chip>
      </h5>
      <v-tooltip v-if="!readonly" top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            small
            color="primary"
            :loading="translating"
            :disabled="translating || !canTranslate"
            :aria-label="translateLabel"
            v-bind="attrs"
            v-on="on"
            @click="translateMissing"
          >
            <v-icon small>mdi-translate</v-icon>
          </v-btn>
        </template>
        {{ translateLabel }}
      </v-tooltip>
    </div>

    <v-row class="multilingual-text-field__language-row" align="center" no-gutters>
      <v-col>
        <v-tabs v-model="activeLanguage" class="multilingual-text-field__tabs" grow>
          <v-tab v-for="language in languages" :key="language.value">
            {{ $t(language.labelKey) }}
            <v-icon v-if="hasContent(language.value)" x-small class="ml-1">mdi-check</v-icon>
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <v-tabs-items v-model="activeLanguage">
      <v-tab-item v-for="language in languages" :key="language.value">
        <v-text-field
          v-if="!rich"
          :value="valueFor(language.value)"
          :rules="rules"
          :readonly="readonly"
          :hint="hint"
          :persistent-hint="persistentHint && !readonly"
          outlined
          @input="updateLanguage(language.value, $event)"
        />
        <EditorText
          v-else-if="editorReady"
          :key="`${field}-${language.value}`"
          :text="valueFor(language.value)"
          :language="language.value"
          :field="field"
          :auto-grow="true"
          :disabled="readonly"
          @change-content="updateRichLanguage"
        />
      </v-tab-item>
    </v-tabs-items>

    <v-input
      v-if="required && !readonly"
      :value="hasAnyContent"
      :rules="[value => value || $t('forms.field.validation.required')]"
      class="multilingual-text-field__required-validation"
    />
  </div>
</template>

<script>
import axios from "axios";

import EditorText from "@/components/reusables/EditorText.vue";
import {
  MULTILINGUAL_LANGUAGES,
  multilingualHasContent,
  normalizeMultilingualValue
} from "@/utils/multilingual";

export default {
  name: "MultilingualTextField",
  components: { EditorText },
  props: {
    value: { type: [String, Object], default: () => ({}) },
    label: { type: String, required: true },
    rules: { type: Array, default: () => [] },
    required: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    hint: { type: String, default: "" },
    persistentHint: { type: Boolean, default: false },
    rich: { type: Boolean, default: false },
    entityType: { type: String, required: true },
    field: { type: String, required: true },
    editorReady: { type: Boolean, default: true }
  },
  data() {
    return { activeLanguage: 0, translating: false };
  },
  mounted() {
    const populatedLanguage = this.languages.findIndex(language => this.hasContent(language.value));
    if (populatedLanguage >= 0) {
      this.activeLanguage = populatedLanguage;
      return;
    }
    const currentLanguage = this.languages.findIndex(
      language => language.value === this.$i18n.locale
    );
    if (currentLanguage >= 0) {
      this.activeLanguage = currentLanguage;
    }
  },
  computed: {
    languages() {
      return MULTILINGUAL_LANGUAGES;
    },
    normalizedValue() {
      return normalizeMultilingualValue(this.value);
    },
    activeLanguageCode() {
      return this.languages[this.activeLanguage].value;
    },
    hasAnyContent() {
      return multilingualHasContent(this.value);
    },
    sourceText() {
      return this.valueFor(this.activeLanguageCode);
    },
    canTranslate() {
      return Boolean(this.sourceText && this.sourceText.trim()) &&
        this.languages.some(language => (
          language.value !== this.activeLanguageCode && !this.hasContent(language.value)
        ));
    },
    translateLabel() {
      return this.$t("form.translate_missing_languages", {
        language: this.$t(`form.multilingual_languages.${this.activeLanguageCode}`)
      });
    }
  },
  methods: {
    valueFor(language) {
      return this.normalizedValue[language] || "";
    },
    hasContent(language) {
      return Boolean(this.valueFor(language).replace(/<[^>]+>/g, "").trim());
    },
    updateLanguage(language, value) {
      this.$emit("input", { ...this.normalizedValue, [language]: value || "" });
    },
    updateRichLanguage(language, content) {
      this.updateLanguage(language, content || "");
    },
    translateMissing() {
      if (!this.canTranslate) {
        return;
      }

      const targetLanguages = this.languages
        .filter(language => language.value !== this.activeLanguageCode && !this.hasContent(language.value))
        .map(language => language.value);
      this.translating = true;
      axios.post(`${this.$store.state.endpoint}/${this.entityType}/translate`, {
        field: this.field,
        source_language: this.activeLanguageCode,
        source_text: this.sourceText,
        values: this.normalizedValue,
        target_languages: targetLanguages
      }).then(response => {
        this.$emit("input", {
          ...this.normalizedValue,
          ...(response.data.translations || {})
        });
        this.$store.commit("alert", {
          color: "success",
          text: this.$t("form.translation_completed")
        });
      }).catch(error => {
        const detail = error.response && error.response.data && error.response.data.detail;
        this.$store.commit("alert", {
          color: "error",
          text: detail || this.$t("form.translation_unavailable")
        });
      }).finally(() => {
        this.translating = false;
      });
    }
  }
};
</script>

<style scoped>
.multilingual-text-field__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.multilingual-text-field__heading h5 {
  flex: 1 1 auto;
}

.multilingual-text-field__tabs {
  margin-bottom: 0.5rem;
}

.multilingual-text-field__required-validation {
  margin-top: -0.5rem;
}
</style>
